import apiClient from './apiClient';

/**
 * Cache für Referenzdaten (Policies, Clauses, Enum-Typen) mit Time-to-Live.
 * Reduziert redundante API-Aufrufe im Wizard (Steps laden dieselben Daten mehrfach).
 * Pattern analog zu tenantService.js.
 *
 * Gibt stets defensive Kopien zurück, damit Aufrufer (z.B. .sort()) den Cache nicht mutieren.
 * Dedupliziert parallele Requests auf denselben Endpoint (Cache-Stampede-Schutz).
 *
 * Features:
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - ETag-Support (304 Not Modified)
 * - 1h TTL (Produktionsanforderung)
 */
const CACHE_VERSION = 'v2';
const CACHE_PREFIX = `referenceData:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 Stunde Cache-Laufzeit (Produktionsanforderung)

// In-Memory Cache als Fallback (Private Mode, localStorage nicht verfügbar)
const memoryCache = new Map();
const pendingRequests = new Map();

// Invalidation-Token für Race-Condition-Schutz bei clearCache()
let cacheInvalidationToken = 0;

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const hasValidData = (data, validateData) => {
  return typeof validateData === 'function' ? validateData(data) : true;
};

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * Liest Cache-Eintrag aus localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @returns {object|null} Cache-Eintrag oder null wenn nicht vorhanden/ungültig
 */
const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    // Validierung der Struktur
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    // Bei JSON-Fehler: Cache-Eintrag ignorieren
    return null;
  }
};

/**
 * Schreibt Cache-Eintrag in localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @param {object} value - Cache-Wert { data, timestamp, etag }
 */
const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded oder anderer Fehler - Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

/**
 * Löscht alle Cache-Einträge aus localStorage.
 * Verwendet atomares Löschen (keine Array-Zwischenspeicherung).
 */
const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    // Rückwärts iterieren und direkt löschen (atomar, kein Array)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf Referenzdaten-Endpoints.
 * Unterstützt localStorage-Persistenz und ETag-basierte Revalidierung.
 * @param {string} url - API-Pfad (z.B. '/pet-policies')
 * @param {object} [params] - Query-Parameter (z.B. { names: 'clause1,clause2' })
 * @param {AbortSignal} [signal] - AbortController-Signal (nur bei echtem API-Call)
 * @returns {Promise<Array|object>} Die Referenzdaten (defensive Kopie bei Arrays)
 */
const getCached = (url, params, signal, _retryCount = 0, options = {}) => {
  const { validateData, forceRefresh = false } = options;
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  if (forceRefresh) {
    invalidateCacheKey(cacheKey);
  }

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCachedRaw = memoryCache.get(cacheKey);
  const memoryCached = isFresh(memoryCachedRaw) && hasValidData(memoryCachedRaw.data, validateData)
    ? memoryCachedRaw
    : null;
  if (memoryCachedRaw && !memoryCached) {
    memoryCache.delete(cacheKey);
  }
  if (memoryCached) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }

  // 2. Prüfe localStorage-Cache
  const localCachedRaw = getFromLocalStorage(cacheKey);
  const localCached = isFresh(localCachedRaw) && hasValidData(localCachedRaw.data, validateData)
    ? localCachedRaw
    : null;
  if (localCachedRaw && !localCached) {
    removeFromLocalStorage(cacheKey);
  }
  if (localCached) {
    // Wiederbefüllen des Memory-Cache
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  // WICHTIG: Deduplizierte Caller nutzen das Promise OHNE Signal-Sharing
  // (verhindert Race Condition wenn Caller A abbricht und Caller B den Fehler bekommt)
  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;
  // ETag senden falls vorhanden (Revalidierung)
  const etag = localCached?.etag || memoryCached?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  // Token für Cache-Invalidation-Race-Schutz
  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache() aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = (localCached || memoryCached)?.data;
        if (cachedData && (!Array.isArray(cachedData) || cachedData.length > 0)) {
          return cachedData;
        }
        // Cache wurde während des Requests geleert — Retry ohne ETag (max. 1x)
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1, options);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || []);
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      const cached = localCached || memoryCached;

      // SAFETY-CHECK: Falls Cache zwischenzeitlich gelöscht wurde (Multi-Tab, Race)
      if (!cached || !hasValidData(cached.data, validateData)) {
        pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          // Cache-Eintrag fehlt - Retry ohne ETag (sollte 200 zurückgeben)
          return getCached(url, params, signal, _retryCount + 1, options).then(data =>
            Array.isArray(data) ? [...data] : data
          );
        }
        return [];
      }

      const refreshedEntry = {
        data: cached.data,
        timestamp: Date.now(),
        etag: cached.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      pendingRequests.delete(cacheKey);
      return cached.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    if (hasValidData(data, validateData)) {
      const newEntry = {
        data,
        timestamp: Date.now(),
        etag: responseEtag
      };
      memoryCache.set(cacheKey, newEntry);
      setToLocalStorage(cacheKey, newEntry);
    } else {
      invalidateCacheKey(cacheKey);
    }
    pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

const nonEmptyArray = (data) => Array.isArray(data) && data.length > 0;
const referenceListOptions = (options) => ({
  validateData: nonEmptyArray,
  ...options
});

const referenceDataService = {
  getPetPolicies: (signal, options) => getCached('/pet-policies', undefined, signal, 0, referenceListOptions(options)),
  getCommercialUsePolicies: (signal, options) => getCached('/commercial-use-policies', undefined, signal, 0, referenceListOptions(options)),
  getCosmeticRepairsPolicies: (signal, options) => getCached('/cosmetic-repairs-policies', undefined, signal, 0, referenceListOptions(options)),
  getMinorRepairsClauses: (signal, options) => getCached('/minor-repairs-clauses', undefined, signal, 0, referenceListOptions(options)),
  getFittedKitchenClauses: (signal, options) => getCached('/fitted-kitchen-clauses', undefined, signal, 0, referenceListOptions(options)),
  getEnergeticRenovationClauses: (signal, options) => getCached('/energetic-renovation-clauses', undefined, signal, 0, referenceListOptions(options)),
  getInsuranceClauses: (signal, options) => getCached('/insurance-clauses', undefined, signal, 0, referenceListOptions(options)),
  getBalconyPowerPlantClauses: (signal, options) => getCached('/balcony-power-plant-clauses', undefined, signal, 0, referenceListOptions(options)),
  getLandlordInspectionClauses: (signal, options) => getCached('/landlord-inspection-clauses', undefined, signal, 0, referenceListOptions(options)),
  getClauseTexts: (names, signal) => getCached('/clause-texts', { names }, signal),
  getKeyTypes: (signal, options) => getCached('/key-types', undefined, signal, 0, referenceListOptions(options)),
  getRenovationStatuses: (signal, options) => getCached('/renovation-statuses', undefined, signal, 0, referenceListOptions(options)),
  getCosmeticRepairsExecutionTypes: (signal, options) => getCached('/cosmetic-repairs-execution-types', undefined, signal, 0, referenceListOptions(options)),

  /**
   * Sucht Bankdaten (BIC, Bankname) anhand der Bankleitzahl (BLZ).
   * Profitiert vom vollständigen Cache-Stack: Memory-Cache, localStorage-Persistenz (1h TTL),
   * ETag-Support und Request-Deduplizierung.
   * @param {string} blz - 8-stellige Bankleitzahl
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<{bic: string, bankName: string}>}
   */
  lookupBankByBlz: (blz, signal) => getCached(`/bank-directory/lookup/${blz}`, undefined, signal),

  /**
   * Sucht Städte anhand einer 5-stelligen Postleitzahl.
   * Gibt eine Liste von Einträgen zurück (eine PLZ kann mehrere Städte haben).
   * @param {string} plz - 5-stellige Postleitzahl
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<Array<{postalCode: string, city: string, state: string}>>}
   */
  lookupPostalCode: (plz, signal) => getCached(`/postal-directory/lookup/${plz}`, undefined, signal),

  /**
   * Sucht PLZ-Einträge anhand eines Stadtnamens-Präfixes (mind. 3 Zeichen).
   * Gibt maximal 20 Treffer zurück.
   * @param {string} prefix - Stadtnamens-Präfix (mind. 3 Zeichen)
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<Array<{postalCode: string, city: string, state: string}>>}
   */
  searchCityByPrefix: (prefix, signal) => getCached('/postal-directory/search', { city: prefix }, signal),

  /**
   * Löscht den kompletten Referenzdaten-Cache (Memory + localStorage).
   * Aufrufen bei Logout.
   * Invalidiert auch laufende Requests (diese schreiben nicht mehr in den Cache).
   */
  clearCache: () => {
    cacheInvalidationToken++; // Laufende Requests ignorieren
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default referenceDataService;
